import React, { useEffect, useState, useCallback } from "react";
import "./ExcelBatchPage.css";
import { API_BASE as _API_BASE } from "../api/config";

const API_BASE = `${_API_BASE}/excel-batch/api`;

/**
 * ExcelBatchPage - Massenverarbeitung CSV → Excel aus Dropbox
 * 
 * Workflow:
 * 1. Stats laden (Anzahl CSV/Excel-Dateien)
 * 2. Preview laden (Matching, Mapping-Status, Datumsvalidierung)
 * 3. Bei fehlendem Mapping: "Mapping hinzufügen"-Modal öffnen
 * 4. Batch starten (nur "ready" Einträge werden verarbeitet)
 * 5. Nach Excel-Batch: Popup "Auch in Datenbank importieren?"
 * 6. NEU: Bei "Ganzes Jahr ersetzen" werden auch skip_date mit full_year verarbeitet
 * 7. NEU: Admin-Modal mit Validierung
 */
const ExcelBatchPage = ({ isOpen, onClose }) => {
  // === State ===
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [preview, setPreview] = useState(null);
  const [batchId, setBatchId] = useState(null);
  const [trace, setTrace] = useState([]);
  
  // Batch-Run State
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  
  // Replace Full Year State
  const [replaceFullYear, setReplaceFullYear] = useState(false);
  
  // DB Import Popup State (nach Excel-Batch)
  const [pendingBatchResult, setPendingBatchResult] = useState(null);
  const [importingDb, setImportingDb] = useState(false);
  const [finalizingBatch, setFinalizingBatch] = useState(false);
  
  // Admin Modal State
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  
  // Mapping Modal State
  const [mappingModal, setMappingModal] = useState(null);
  const [mappingFields, setMappingFields] = useState(null);
  const [mappingValues, setMappingValues] = useState({});
  const [savingMapping, setSavingMapping] = useState(false);
  // Neue Excel für CSV ohne passende Excel anlegen
  const [createExcelModal, setCreateExcelModal] = useState(null); // Match-Objekt
  const [createExcelForm, setCreateExcelForm] = useState({ filename: "", sheet_name: "", iban: "" });
  const [createExcelError, setCreateExcelError] = useState("");
  const [creatingExcel, setCreatingExcel] = useState(false);

  // Deep-Validate (Vollabgleich) State
  const [deepValidating, setDeepValidating] = useState(false);
  const [deepValidateResult, setDeepValidateResult] = useState(null);
  const [insertingMissing, setInsertingMissing] = useState(null); // match_id das gerade eingefügt wird
  const [selectedMissingRows, setSelectedMissingRows] = useState({}); // {match_id: Set<index>}
  const [noCsvExpanded, setNoCsvExpanded] = useState(false); // no_csv Einträge eingeklappt
  const [forceGapIds, setForceGapIds] = useState(new Set()); // Match-IDs für erzwungene Datenlücken-Einfügung
  // Welle 76: Konto ohne Excel dauerhaft als „Nur DB" merken (Bestätigungs-Popup)
  const [dbOnlyModal, setDbOnlyModal] = useState(null); // Match-Objekt
  const [savingDbOnly, setSavingDbOnly] = useState(false);

  // === Stats laden beim Öffnen ===
  useEffect(() => {
    if (isOpen) {
      loadStats();
    } else {
      // Reset beim Schließen
      setStats(null);
      setPreview(null);
      setBatchId(null);
      setTrace([]);
      setResult(null);
      setError(null);
      setReplaceFullYear(false);
      setForceGapIds(new Set());
      setPendingBatchResult(null);
      setAdminModalOpen(false);
      setValidationResult(false);
    }
  }, [isOpen]);

  // === API Calls ===
  const loadStats = async () => {
    setLoadingStats(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/stats`);
      const data = await res.json();
      setStats(data);
      setTrace(data.trace || []);
      
      if (!data.success) {
        setError("Dropbox-Verbindung fehlgeschlagen");
      }
    } catch (e) {
      setError(`Fehler beim Laden: ${e.message}`);
    } finally {
      setLoadingStats(false);
    }
  };

  const loadPreview = async () => {
    setLoadingPreview(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/preview`);
      const data = await res.json();
      setPreview(data);
      setBatchId(data.batch_id);
      setTrace(data.trace || []);
      
      if (!data.success) {
        setError("Preview konnte nicht geladen werden");
      }
    } catch (e) {
      setError(`Fehler beim Laden: ${e.message}`);
    } finally {
      setLoadingPreview(false);
    }
  };

  const runBatch = async () => {
    if (!batchId) return;
    
    setRunning(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/run`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          batch_id: batchId,
          replace_full_year: replaceFullYear,
          force_gap_ids: Array.from(forceGapIds)
        }),
      });
      const data = await res.json();
      setTrace(data.trace || []);
      
      if (!data.success) {
        setError("Batch-Verarbeitung fehlgeschlagen");
        setResult(data);
      } else if (data.pending_db_import) {
        // Excel-Teil fertig → DB-Import läuft immer automatisch (kein Popup mehr)
        setPendingBatchResult(data);
        await runDbImport(data);
      } else {
        // Kein DB-Import pending, direkt finalisieren
        await finalizeBatch(data);
      }
    } catch (e) {
      setError(`Fehler bei Batch: ${e.message}`);
    } finally {
      setRunning(false);
    }
  };

  // DB-Import durchführen (batchResult als Parameter, da der State-Wert
  // beim automatischen Aufruf direkt nach dem Batch noch nicht gesetzt ist)
  const runDbImport = async (batchResult = pendingBatchResult) => {
    if (!batchId) return;

    setImportingDb(true);
    try {
      const res = await fetch(`${API_BASE}/run-db-import`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ batch_id: batchId }),
      });
      const data = await res.json();

      // Merge DB-Import Ergebnis mit vorherigem Batch-Ergebnis
      const mergedResult = {
        ...batchResult,
        summary: {
          ...batchResult.summary,
          db_inserted: data.db_inserted ?? data.summary?.db_inserted ?? 0,
          db_skipped: data.db_skipped ?? data.summary?.db_skipped ?? 0,
        },
        db_errors: data.db_errors || [],
        db_results: data.results || [],
      };
      
      setTrace(prev => [...prev, ...(data.trace || [])]);
      
      // Batch finalisieren (ZIP + Cleanup)
      await finalizeBatch(mergedResult);
      
    } catch (e) {
      setError(`Fehler beim DB-Import: ${e.message}`);
    } finally {
      setImportingDb(false);
    }
  };

  // Batch finalisieren (ZIP + Cleanup)
  const finalizeBatch = async (batchResult) => {
    setFinalizingBatch(true);
    try {
      const res = await fetch(`${API_BASE}/finalize-batch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ batch_id: batchId }),
      });
      const data = await res.json();
      
      // Finales Ergebnis mit Archiv-Info
      const finalResult = {
        ...batchResult,
        csv_archive: data.csv_archive,
      };
      
      setTrace(prev => [...prev, ...(data.trace || [])]);
      setResult(finalResult);
      
    } catch (e) {
      setError(`Fehler beim Finalisieren: ${e.message}`);
      setResult(batchResult);  // Zeige zumindest das bisherige Ergebnis
    } finally {
      setFinalizingBatch(false);
      setPendingBatchResult(null);
    }
  };

  // === Validierung ===
  const runValidation = async () => {
    if (!batchId) return;
    
    setValidating(true);
    setValidationResult(null);
    try {
      const res = await fetch(`${API_BASE}/validate-all`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ batch_id: batchId }),
      });
      const data = await res.json();
      setValidationResult(data);
      
      if (!data.success) {
        setError("Validierung fehlgeschlagen");
      }
    } catch (e) {
      setError(`Fehler bei Validierung: ${e.message}`);
    } finally {
      setValidating(false);
    }
  };

  // === Deep-Validate (Vollabgleich) ===
  const runDeepValidate = async () => {
    if (!batchId) return;
    
    setDeepValidating(true);
    setDeepValidateResult(null);
    try {
      const res = await fetch(`${API_BASE}/deep-validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ batch_id: batchId }),
      });
      const data = await res.json();
      setDeepValidateResult(data);
      // Alle missing rows standardmäßig auswählen
      if (data?.results) {
        const sel = {};
        data.results.forEach(r => {
          if (r.missing_rows?.length > 0) {
            sel[r.match_id] = new Set(r.missing_rows.map((_, i) => i));
          }
        });
        setSelectedMissingRows(sel);
      }
      
      if (!data.success) {
        setError("Vollabgleich fehlgeschlagen");
      }
    } catch (e) {
      setError(`Fehler beim Vollabgleich: ${e.message}`);
    } finally {
      setDeepValidating(false);
    }
  };

  // === Toggle einzelne missing row Checkbox ===
  const toggleMissingRow = (matchId, rowIdx) => {
    setSelectedMissingRows(prev => {
      const s = new Set(prev[matchId] || []);
      if (s.has(rowIdx)) s.delete(rowIdx); else s.add(rowIdx);
      return { ...prev, [matchId]: s };
    });
  };

  // === Alle missing rows eines Matches an/abwählen ===
  const toggleAllMissingRows = (matchId, total) => {
    setSelectedMissingRows(prev => {
      const current = prev[matchId] || new Set();
      const allSelected = current.size === total;
      return { ...prev, [matchId]: allSelected ? new Set() : new Set(Array.from({length: total}, (_, i) => i)) };
    });
  };

  // === Fehlende Buchungen einfügen ===
  const insertMissingRows = async (matchId) => {
    if (!batchId || !matchId) return;
    
    const selected = selectedMissingRows[matchId];
    if (!selected || selected.size === 0) {
      setError("Keine Zeilen ausgewählt zum Einfügen");
      return;
    }
    
    setInsertingMissing(matchId);
    try {
      const res = await fetch(`${API_BASE}/insert-missing`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          batch_id: batchId, 
          match_id: matchId,
          rows_to_insert: Array.from(selected).sort((a, b) => a - b),
        }),
      });
      const data = await res.json();
      
      if (data.success) {
        // Ergebnis aktualisieren — diesen Match als OK markieren
        setDeepValidateResult(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            results: prev.results.map(r => 
              r.match_id === matchId 
                ? { ...r, valid: true, missing_rows: [], total_missing: 0, 
                    _inserted: data.rows_inserted, _insert_message: data.message }
                : r
            ),
            summary: {
              ...prev.summary,
              valid: (prev.summary?.valid || 0) + 1,
              invalid: Math.max(0, (prev.summary?.invalid || 0) - 1),
              total_missing_rows: (prev.summary?.total_missing_rows || 0) - 
                (prev.results.find(r => r.match_id === matchId)?.total_missing || 0),
            },
          };
        });
      } else {
        setError(`Einfügen fehlgeschlagen: ${data.error}`);
      }
    } catch (e) {
      setError(`Fehler beim Einfügen: ${e.message}`);
    } finally {
      setInsertingMissing(null);
    }
  };

  // === Mapping Modal ===
  const openMappingModal = async (match) => {
    setMappingModal(match);
    setMappingValues({});
    
    try {
      const res = await fetch(`${API_BASE}/mapping-fields/${batchId}/${match.id}`);
      const data = await res.json();
      setMappingFields(data);
      
      // Mapping initialisieren - genau wie bei MappingPage:
      // 1. Wenn savedMapping vorhanden → verwende es
      // 2. Sonst → exakter Match (csvHeader === excelHeader)
      const defaults = {};
      if (data.saved_mapping && Object.keys(data.saved_mapping).length > 0) {
        // Gespeichertes Mapping verwenden
        for (const header of data.excel_headers) {
          defaults[header] = data.saved_mapping[header] || "";
        }
      } else {
        // Kein gespeichertes Mapping → nur exakter Match wie bei MappingPage
        for (const header of data.excel_headers) {
          defaults[header] = data.csv_headers.find(csvHeader => csvHeader === header) || "";
        }
      }
      setMappingValues(defaults);
    } catch (e) {
      setError(`Mapping-Felder konnten nicht geladen werden: ${e.message}`);
    }
  };

  const closeMappingModal = () => {
    setMappingModal(null);
    setMappingFields(null);
    setMappingValues({});
  };

  /* Neue Excel anlegen (no_excel-Match) → Match wird needs_mapping,
     danach öffnet direkt das Mapping-Modal. */
  const createExcelHandler = async () => {
    if (!createExcelModal || !batchId) return;
    setCreateExcelError("");
    setCreatingExcel(true);
    try {
      const res = await fetch(`${API_BASE}/create-excel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          batch_id: batchId,
          match_id: createExcelModal.id,
          filename: createExcelForm.filename,
          sheet_name: createExcelForm.sheet_name,
          iban: createExcelForm.iban,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      // Match im Preview-State ersetzen + Summary neu berechnen
      let updatedMatch = null;
      setPreview(prev => {
        if (!prev) return prev;
        const updatedMatches = prev.matches.map(m => {
          if (m.id === createExcelModal.id) { updatedMatch = data.match; return data.match; }
          return m;
        });
        const newSummary = {
          total: updatedMatches.length,
          ready: updatedMatches.filter(m => m.final_status === "ready").length,
          needs_mapping: updatedMatches.filter(m => m.final_status === "needs_mapping").length,
          skip_date: updatedMatches.filter(m => m.final_status === "skip_date").length,
          no_excel: updatedMatches.filter(m => m.final_status === "no_excel").length,
          no_csv: updatedMatches.filter(m => m.final_status === "no_csv").length,
        };
        return { ...prev, matches: updatedMatches, summary: newSummary };
      });
      setCreateExcelModal(null);
      // Direkt das Mapping anlegen (Spalte A "Auftragskonto" wird per
      // Exakt-Match automatisch vorausgewählt)
      if (updatedMatch) openMappingModal(updatedMatch);
    } catch (e) {
      setCreateExcelError(e.message);
    } finally {
      setCreatingExcel(false);
    }
  };

  const saveMappingHandler = async () => {
    if (!mappingModal || !batchId) return;
    
    setSavingMapping(true);
    try {
      const res = await fetch(`${API_BASE}/add-mapping`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          batch_id: batchId,
          match_id: mappingModal.id,
          mappings: mappingValues,
        }),
      });
      const data = await res.json();
      
      if (data.success) {
        // Nur den einen Match im lokalen State aktualisieren (nicht alles neu laden!)
        setPreview(prev => {
          if (!prev) return prev;
          const updatedMatches = prev.matches.map(m => {
            if (m.id === mappingModal.id) {
              // Match aktualisieren: has_mapping=true, final_status anpassen
              const newStatus = m.date_valid ? "ready" : "skip_date";
              return {
                ...m,
                has_mapping: true,
                final_status: newStatus,
              };
            }
            return m;
          });
          
          // Summary neu berechnen
          const newSummary = {
            total: updatedMatches.length,
            ready: updatedMatches.filter(m => m.final_status === "ready").length,
            needs_mapping: updatedMatches.filter(m => m.final_status === "needs_mapping").length,
            skip_date: updatedMatches.filter(m => m.final_status === "skip_date").length,
            no_excel: updatedMatches.filter(m => m.final_status === "no_excel").length,
            no_csv: updatedMatches.filter(m => m.final_status === "no_csv").length,
          };
          
          return {
            ...prev,
            matches: updatedMatches,
            summary: newSummary,
          };
        });
        closeMappingModal();
      } else {
        setError(data.detail || "Mapping speichern fehlgeschlagen");
      }
    } catch (e) {
      setError(`Fehler beim Speichern: ${e.message}`);
    } finally {
      setSavingMapping(false);
    }
  };

  const handleMappingChange = (excelHeader, csvHeader) => {
    setMappingValues(prev => ({
      ...prev,
      [excelHeader]: csvHeader,
    }));
  };

  /* Welle 76: Nur-DB-Merkmal für das Konto einer CSV setzen/aufheben.
     Backend schreibt konto_code_map.nur_db und klassifiziert den Match neu;
     danach Preview komplett neu laden (Summary + Log-Status). */
  const setDbOnly = async (match, nurDb) => {
    if (!batchId || !match) return;
    setSavingDbOnly(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/set-db-only`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ batch_id: batchId, match_id: match.id, nur_db: nurDb }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || `HTTP ${res.status}`);
      setTrace(prev => [...prev, ...(data.trace || [])]);
      setDbOnlyModal(null);
      await loadPreview();
    } catch (e) {
      setError(`Nur-DB konnte nicht gespeichert werden: ${e.message}`);
    } finally {
      setSavingDbOnly(false);
    }
  };

  // === Status-Badge Komponente ===
  const StatusBadge = ({ status }) => {
    const statusConfig = {
      ready: { label: "Bereit", className: "status-ready" },
      needs_mapping: { label: "Mapping fehlt", className: "status-warning" },
      skip_date: { label: "Übersprungen (Datum)", className: "status-skip" },
      no_excel: { label: "Excel fehlt", className: "status-error" },
      db_only: { label: "Nur DB", className: "status-db" },
      no_csv: { label: "CSV fehlt", className: "status-skip" },
      duplicate_source: { label: "Ältere Doppel-Quelle", className: "status-skip" },
      matched: { label: "Gematcht", className: "status-info" },
    };
    
    const config = statusConfig[status] || { label: status, className: "status-unknown" };
    
    return (
      <span className={`status-badge ${config.className}`}>
        {config.label}
      </span>
    );
  };

  // === Loading Spinner Komponente ===
  const LoadingSpinner = ({ size = "medium", text, subtext }) => (
    <div className="loading">
      <div className={`loading-spinner ${size}`}></div>
      {text && <div className="loading-text">{text}</div>}
      {subtext && <div className="loading-subtext">{subtext}</div>}
    </div>
  );

  // === Render ===
  if (!isOpen) return null;

  return (
    <div className="excel-batch-overlay">
      <div className="excel-batch-modal">
        {/* Header */}
        <div className="excel-batch-header">
          <h2>Excel Batch - Dropbox</h2>
          <button className="close-btn" onClick={onClose} disabled={running || loadingPreview || loadingStats}>
            ×
          </button>
        </div>

        {/* Content */}
        <div className="excel-batch-content">
          {/* Error */}
          {error && (
            <div className="error-box">
              <span>❌</span> {error}
              <button onClick={() => setError(null)}>×</button>
            </div>
          )}

          {/* Loading für Preview */}
          {loadingPreview && !preview && (
            <div className="loading">
              <div className="loading-spinner"></div>
              <div className="loading-text">Analysiere Dateien...</div>
              <div className="loading-subtext">Dies kann bei vielen Dateien etwas dauern</div>
            </div>
          )}

          {/* Loading für Batch-Run */}
          {running && (
            <div className="loading">
              <div className="loading-spinner"></div>
              <div className="loading-text">Verarbeite Batch...</div>
              <div className="loading-subtext">Bitte warten, Daten werden in Excel eingefügt</div>
            </div>
          )}

          {/* Loading für DB-Import */}
          {importingDb && (
            <div className="loading">
              <div className="loading-spinner"></div>
              <div className="loading-text">Importiere in Datenbank...</div>
              <div className="loading-subtext">CSV-Daten werden in die Datenbank geschrieben</div>
            </div>
          )}

          {/* Loading für Finalisierung */}
          {finalizingBatch && (
            <div className="loading">
              <div className="loading-spinner"></div>
              <div className="loading-text">Finalisiere Batch...</div>
              <div className="loading-subtext">CSV-Dateien werden archiviert</div>
            </div>
          )}

          {/* Stats View (Initial) */}
          {!preview && !result && !loadingPreview && (
            <div className="stats-view">
              <h3>Dropbox-Ordner</h3>
              
              {loadingStats ? (
                <div className="loading">
                  <div className="loading-spinner"></div>
                  <div className="loading-text">Lade Statistiken...</div>
                  <div className="loading-subtext">Verbinde mit Dropbox</div>
                </div>
              ) : stats ? (
                <>
                  <div className="stats-grid">
                    <div className="stat-card">
                      <div className="stat-value">{stats.csv_count}</div>
                      <div className="stat-label">CSV-Dateien</div>
                      <div className="stat-path">{stats.csv_folder}</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-value">{stats.excel_count}</div>
                      <div className="stat-label">Excel-Dateien</div>
                      <div className="stat-path">{stats.excel_folder}</div>
                    </div>
                  </div>

                  <div className="action-buttons">
                    <button 
                      className="btn-primary"
                      onClick={loadPreview}
                      disabled={loadingPreview || stats.csv_count === 0}
                    >
                      {loadingPreview ? (
                        <span className="btn-loading">
                          <div className="loading-spinner small"></div>
                          Analysiere...
                        </span>
                      ) : "Dateien analysieren"}
                    </button>
                    <button 
                      className="btn-secondary"
                      onClick={loadStats}
                      disabled={loadingStats}
                    >
                      {loadingStats ? (
                        <span className="btn-loading">
                          <div className="loading-spinner small"></div>
                          Lädt...
                        </span>
                      ) : "Aktualisieren"}
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          )}

          {/* Preview View */}
          {preview && !result && !running && !importingDb && !finalizingBatch && (
            <div className="preview-view">
              <div className="preview-header">
                <h3>Vorschau</h3>
                <button 
                  className="btn-admin"
                  onClick={() => setAdminModalOpen(true)}
                  title="Admin-Funktionen öffnen"
                >
                  ⚙️ Admin
                </button>
              </div>
              
              {/* Summary */}
              <div className="summary-bar">
                <span className="summary-item ready">
                  ✓ {preview.summary?.ready || 0} bereit
                </span>
                {(preview.summary?.db_only || 0) > 0 && (
                  <span className="summary-item db" title="Nur-DB-Konten: kein Excel, direkt in die Datenbank">
                    🗄 {preview.summary.db_only} nur DB
                  </span>
                )}
                <span className="summary-item warning">
                  ⚠ {preview.summary?.needs_mapping || 0} Mapping fehlt
                </span>
                <span className="summary-item skip">
                  ↷ {preview.summary?.skip_date || 0} übersprungen
                  {replaceFullYear && preview.summary?.full_year_detected > 0 && (
                    <span className="skip-override"> (davon {preview.summary.full_year_detected} werden ersetzt)</span>
                  )}
                </span>
                <span className="summary-item error">
                  ✗ {(preview.summary?.no_excel || 0) + (preview.summary?.no_csv || 0)} unvollständig
                </span>
              </div>

              {/* Match Table */}
              <div className="match-table-wrapper">
                <table className="match-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>CSV-Datei</th>
                      <th>IBAN (CSV)</th>
                      <th>Excel-Datei</th>
                      <th>Sheet</th>
                      <th>Datumsbereich</th>
                      <th>Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const allMatches = preview.matches || [];
                      const visibleMatches = allMatches.filter(m => m.final_status !== "no_csv");
                      const noCsvMatches = allMatches.filter(m => m.final_status === "no_csv");
                      
                      const renderRow = (match) => {
                        const fullYearInfo = match.full_year_info;
                        const isFullYear = fullYearInfo?.is_full_year;
                        const fullYearNum = fullYearInfo?.year;
                        const extraYears = fullYearInfo?.extra_years || [];
                        const willBeReplaced = replaceFullYear && isFullYear && match.final_status === "skip_date";
                        
                        return (
                          <tr 
                            key={match.id} 
                            className={`row-${match.final_status}${willBeReplaced ? ' row-will-replace' : ''}`}
                          >
                          <td>
                            <StatusBadge status={match.final_status} />
                            {willBeReplaced && (
                              <span className="status-badge status-replace">→ Ersetzen</span>
                            )}
                          </td>
                          <td>
                            {match.csv_filename || "–"}
                            {isFullYear && (
                              <span className="full-year-badge" title={`Ganzes Jahr ${fullYearNum}${extraYears.length ? ` + ${extraYears.join(', ')}` : ''}`}>
                                📅 {fullYearNum}
                                {extraYears.length > 0 && <span className="extra-years">+{extraYears.join(',')}</span>}
                              </span>
                            )}
                            {match.csv_date_reversed && (
                              <span className="date-reversed-badge" title="Datumsreihenfolge wurde umgedreht (neuestes Datum zuerst)">
                                🔄 umgedreht
                              </span>
                            )}
                          </td>
                          <td className="iban-cell">
                            {match.csv_iban ? (
                              <code>{match.csv_iban}</code>
                            ) : match.csv_filename ? (
                              <span className="no-iban">Keine IBAN</span>
                            ) : "–"}
                          </td>
                          <td>
                            {match.excel_filename || (match.db_only
                              ? <span className="db-only-tag" title="Konto ist als Nur-DB gemerkt — keine Excel">🗄 nur DB</span>
                              : "–")}
                          </td>
                          <td>{match.excel_sheet || "–"}</td>
                          <td>
                            {match.csv_oldest_date && match.csv_newest_date && (
                              <span className="date csv-date">
                                CSV: {match.csv_oldest_date} – {match.csv_newest_date}
                              </span>
                            )}
                            {match.excel_oldest_date && match.excel_newest_date && (
                              <span className="date excel-date">
                                Excel: {match.excel_oldest_date} – {match.excel_newest_date}
                              </span>
                            )}
                            {match.db_only && (
                              <span className="date excel-date">
                                DB: {match.db_newest_date ? `bis ${match.db_newest_date}` : "noch keine Buchungen"}
                              </span>
                            )}
                            {match.date_message && match.final_status === "skip_date" && !willBeReplaced && (
                              <span className="date-warning">{match.date_message}</span>
                            )}
                            {match.gap_auto_resolved && (
                              <span className="date-info" style={{color: '#2e7d32', fontSize: '0.85em'}}>✅ Lücke abgedeckt durch frühere CSV-Ladung</span>
                            )}
                            {willBeReplaced && (
                              <span className="date-replace-info">
                                ⚠️ Jahr {fullYearNum} wird komplett ersetzt
                              </span>
                            )}
                          </td>
                          <td className="action-cell">
                            {match.final_status === "needs_mapping" && (
                              <button 
                                className="btn-small btn-mapping"
                                onClick={() => openMappingModal(match)}
                              >
                                Mapping hinzufügen
                              </button>
                            )}
                            {match.final_status === "no_excel" && match.csv_filename && (
                              <>
                                <button
                                  className="btn-small btn-mapping"
                                  title="Neue Excel-Datei für diese CSV anlegen"
                                  onClick={() => {
                                    const kontocode = match.csv_kontocode || match.csv?.kontocode || "";
                                    setCreateExcelForm({
                                      filename: `${kontocode || "Konto"}_Umsaetze.xlsx`,
                                      sheet_name: kontocode || "Umsaetze",
                                      iban: match.csv_iban || match.csv?.iban || match.csv?.konto || "",
                                    });
                                    setCreateExcelError("");
                                    setCreateExcelModal(match);
                                  }}
                                >
                                  Neue Excel anlegen
                                </button>
                                {match.csv_iban && (
                                  <button
                                    className="btn-small btn-db-only"
                                    title="Konto dauerhaft als Nur-DB merken: CSVs gehen direkt in die Datenbank, keine Excel nötig"
                                    onClick={() => setDbOnlyModal(match)}
                                    disabled={savingDbOnly}
                                  >
                                    🗄 Nur DB
                                  </button>
                                )}
                              </>
                            )}
                            {match.db_only && (match.final_status === "db_only" || match.final_status === "skip_date") && (
                              <button
                                className="btn-small btn-link"
                                title="Nur-DB-Merkmal für dieses Konto aufheben"
                                onClick={() => setDbOnly(match, false)}
                                disabled={savingDbOnly}
                              >
                                ↩ Nur-DB aufheben
                              </button>
                            )}
                            {/* Vollabgleich-Button für skip_date und ready */}
                            {(match.final_status === "skip_date" || match.final_status === "ready") && (
                              <button
                                className="btn-small btn-deep-validate"
                                onClick={() => {
                                  setDeepValidating(true);
                                  setDeepValidateResult(null);
                                  fetch(`${API_BASE}/deep-validate`, {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json" },
                                    body: JSON.stringify({ batch_id: batchId, match_ids: [match.id] }),
                                  })
                                    .then(r => r.json())
                                    .then(data => {
                                      setDeepValidateResult(data);
                                      if (!data.success) setError("Vollabgleich fehlgeschlagen");
                                    })
                                    .catch(e => setError(`Fehler: ${e.message}`))
                                    .finally(() => setDeepValidating(false));
                                }}
                                disabled={deepValidating}
                                title="Prüft ob alle CSV-Buchungen in der Excel vorhanden sind"
                              >
                                {deepValidating ? "..." : "🔍 Vollabgleich"}
                              </button>
                            )}
                            {/* Trotzdem einfügen für skip_date (Datenlücke erzwingen) */}
                            {match.final_status === "skip_date" && !willBeReplaced && (
                              <label className="force-gap-label" style={{display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px', fontSize: '0.85em', cursor: 'pointer'}}>
                                <input
                                  type="checkbox"
                                  checked={forceGapIds.has(match.id)}
                                  onChange={(e) => {
                                    setForceGapIds(prev => {
                                      const next = new Set(prev);
                                      if (e.target.checked) next.add(match.id);
                                      else next.delete(match.id);
                                      return next;
                                    });
                                  }}
                                />
                                <span style={{color: '#e65100'}}>⚠️ Trotzdem einfügen</span>
                              </label>
                            )}
                          </td>
                        </tr>
                      );
                    };
                      
                      return (
                        <>
                          {visibleMatches.map(renderRow)}
                          {noCsvMatches.length > 0 && (
                            <>
                              <tr 
                                className="no-csv-toggle-row"
                                onClick={() => setNoCsvExpanded(prev => !prev)}
                                style={{ cursor: "pointer" }}
                              >
                                <td colSpan={7} style={{ 
                                  textAlign: "left", 
                                  padding: "8px 12px",
                                  background: "#f8f9fa",
                                  fontWeight: 500,
                                  fontSize: "0.9em",
                                  color: "#666",
                                  borderTop: "2px solid #dee2e6"
                                }}>
                                  <span style={{ marginRight: 6 }}>
                                    {noCsvExpanded ? "▼" : "▶"}
                                  </span>
                                  {noCsvMatches.length} Excel ohne CSV-Zuordnung
                                </td>
                              </tr>
                              {noCsvExpanded && noCsvMatches.map(renderRow)}
                            </>
                          )}
                        </>
                      );
                    })()}
                  </tbody>
                </table>
              </div>

              {/* Deep-Validate Ergebnis */}
              {deepValidateResult && deepValidateResult.success && (
                <div className="deep-validate-results">
                  <div className="deep-validate-header">
                    <h4>🔍 Vollabgleich-Ergebnis</h4>
                    <button 
                      className="btn-small btn-close" 
                      onClick={() => setDeepValidateResult(null)}
                    >
                      ✕
                    </button>
                  </div>
                  
                  {/* Summary */}
                  <div className="deep-validate-summary">
                    <span className="dv-ok">✓ {deepValidateResult.summary?.valid || 0} vollständig</span>
                    <span className="dv-missing">✗ {deepValidateResult.summary?.invalid || 0} unvollständig</span>
                    {deepValidateResult.summary?.total_missing_rows > 0 && (
                      <span className="dv-count">
                        ({deepValidateResult.summary.total_missing_rows} fehlende Buchungen)
                      </span>
                    )}
                    {deepValidateResult.summary?.total_extra_rows > 0 && (
                      <span className="dv-warning">
                        ⚠️ {deepValidateResult.summary.total_extra_rows} überschüssige in Excel
                      </span>
                    )}
                    {deepValidateResult.summary?.errors > 0 && (
                      <span className="dv-error">⚠ {deepValidateResult.summary.errors} Fehler</span>
                    )}
                  </div>

                  {/* Per-Match Details */}
                  {deepValidateResult.results?.filter(r => !r.valid || r._inserted).map(result => (
                    <div key={result.match_id} className={`dv-match ${result.valid ? 'dv-match-ok' : 'dv-match-missing'}`}>
                      <div className="dv-match-header">
                        <span className="dv-match-file">
                          {result.csv_file} → {result.excel_file}
                        </span>
                        {result._inserted && (
                          <span className="dv-inserted-badge">
                            ✓ {result._inserted} Zeilen eingefügt
                          </span>
                        )}
                        {!result.valid && result.total_missing > 0 && (
                          <button
                            className="btn-small btn-insert-missing"
                            onClick={() => insertMissingRows(result.match_id)}
                            disabled={insertingMissing === result.match_id || !(selectedMissingRows[result.match_id]?.size > 0)}
                          >
                            {insertingMissing === result.match_id ? (
                              <span className="btn-loading">
                                <div className="loading-spinner small"></div>
                                Füge ein...
                              </span>
                            ) : (
                              `📥 ${selectedMissingRows[result.match_id]?.size || 0} ausgewählte einfügen`
                            )}
                          </button>
                        )}
                        {result.error && (
                          <span className="dv-error-msg">⚠ {result.error}</span>
                        )}
                      </div>

                      {/* Missing Rows Table */}
                      {/* Fehlende Zeilen mit Checkboxen */}
                      {result.missing_rows?.length > 0 && (
                        <div className="dv-missing-table-wrapper">
                          <h5 className="dv-section-title">Fehlende Buchungen (CSV → Excel)</h5>
                          <table className="dv-missing-table">
                            <thead>
                              <tr>
                                <th className="dv-check-col">
                                  <input
                                    type="checkbox"
                                    checked={(selectedMissingRows[result.match_id]?.size || 0) === result.missing_rows.length}
                                    onChange={() => toggleAllMissingRows(result.match_id, result.missing_rows.length)}
                                    title="Alle auswählen / abwählen"
                                  />
                                </th>
                                <th>Datum</th>
                                <th>IBAN</th>
                                <th>Betrag</th>
                                <th>Buchungstext</th>
                              </tr>
                            </thead>
                            <tbody>
                              {result.missing_rows.slice(0, 50).map((row, idx) => (
                                <tr key={idx} className={selectedMissingRows[result.match_id]?.has(idx) ? 'dv-row-selected' : 'dv-row-unselected'}>
                                  <td className="dv-check-col">
                                    <input
                                      type="checkbox"
                                      checked={selectedMissingRows[result.match_id]?.has(idx) || false}
                                      onChange={() => toggleMissingRow(result.match_id, idx)}
                                    />
                                  </td>
                                  <td>{row.date || "–"}</td>
                                  <td><code>{row.iban || "–"}</code></td>
                                  <td className={`amount ${parseFloat(row.amount) < 0 ? 'negative' : 'positive'}`}>
                                    {row.amount != null ? parseFloat(row.amount).toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2}) : "–"}
                                  </td>
                                  <td className="buchungstext-cell">{row.text || "–"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {result.missing_rows.length > 50 && (
                            <div className="dv-more">... und {result.missing_rows.length - 50} weitere</div>
                          )}
                        </div>
                      )}

                      {/* Überschüssige Zeilen in Excel (nicht in CSV) */}
                      {result.extra_rows?.length > 0 && (
                        <div className="dv-extra-table-wrapper">
                          <h5 className="dv-section-title dv-section-warning">
                            ⚠️ {result.total_extra} Buchungen in Excel, die nicht in der CSV sind (im CSV-Zeitraum)
                          </h5>
                          <table className="dv-missing-table dv-extra-table">
                            <thead>
                              <tr>
                                <th>Datum</th>
                                <th>IBAN</th>
                                <th>Betrag</th>
                                <th>Buchungstext</th>
                              </tr>
                            </thead>
                            <tbody>
                              {result.extra_rows.slice(0, 50).map((row, idx) => (
                                <tr key={idx} className="dv-row-extra">
                                  <td>{row.date || "–"}</td>
                                  <td><code>{row.iban || "–"}</code></td>
                                  <td className={`amount ${parseFloat(row.amount) < 0 ? 'negative' : 'positive'}`}>
                                    {row.amount != null ? parseFloat(row.amount).toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2}) : "–"}
                                  </td>
                                  <td className="buchungstext-cell">{row.text || "–"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {result.extra_rows.length > 50 && (
                            <div className="dv-more">... und {result.extra_rows.length - 50} weitere</div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Alle vollständig - Erfolgsmeldung */}
                  {deepValidateResult.summary?.invalid === 0 && deepValidateResult.summary?.errors === 0 && (
                    <div className="dv-all-ok">
                      ✅ Alle CSV-Buchungen sind vollständig in den Excel-Dateien vorhanden.
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="action-buttons">
                <button 
                  className="btn-primary"
                  onClick={runBatch}
                  disabled={running || loadingPreview || (
                    (preview.summary?.ready || 0) === 0 && 
                    (preview.summary?.db_only || 0) === 0 &&
                    (preview.summary?.skip_date || 0) === 0 &&
                    forceGapIds.size === 0 &&
                    !(replaceFullYear && preview.summary?.full_year_detected > 0)
                  )}
                >
                  {running ? (
                    <span className="btn-loading">
                      <div className="loading-spinner small"></div>
                      Verarbeite...
                    </span>
                  ) : (() => {
                    const readyCount = (preview.summary?.ready || 0) + (preview.summary?.db_only || 0);
                    const skipDateCount = preview.summary?.skip_date || 0;
                    const fullYearCount = replaceFullYear ? (preview.summary?.full_year_detected || 0) : 0;
                    const forceCount = forceGapIds.size;
                    
                    if (readyCount === 0 && skipDateCount > 0 && forceCount === 0) {
                      // Nur skip_date - Batch für DB-Import
                      return `Batch starten (${skipDateCount} CSV für DB)`;
                    } else if (fullYearCount > 0) {
                      return `Batch starten (${readyCount + fullYearCount} Dateien)`;
                    } else if (forceCount > 0) {
                      return `Batch starten (${readyCount + forceCount} Dateien, ${forceCount} erzwungen)`;
                    } else {
                      return `Batch starten (${readyCount} Dateien)`;
                    }
                  })()}
                </button>
                <button 
                  className="btn-secondary"
                  onClick={loadPreview}
                  disabled={running || loadingPreview}
                >
                  {loadingPreview ? (
                    <span className="btn-loading">
                      <div className="loading-spinner small"></div>
                      Lädt...
                    </span>
                  ) : "Aktualisieren"}
                </button>
                <button 
                  className="btn-secondary"
                  onClick={() => { setPreview(null); setBatchId(null); setDeepValidateResult(null); }}
                  disabled={running}
                >
                  Zurück
                </button>
                <button
                  className="btn-secondary btn-deep-validate-all"
                  onClick={runDeepValidate}
                  disabled={deepValidating || running}
                  title="Alle Zuordnungen auf fehlende Buchungen prüfen"
                >
                  {deepValidating ? (
                    <span className="btn-loading">
                      <div className="loading-spinner small"></div>
                      Prüfe...
                    </span>
                  ) : "🔍 Vollabgleich (alle)"}
                </button>
              </div>
            </div>
          )}

          {/* Result View */}
          {result && (
            <div className="result-view">
              <h3>Ergebnis</h3>
              
              <div className="result-summary">
                <div className="result-card success">
                  <div className="result-value">{result.summary?.processed || 0}</div>
                  <div className="result-label">Verarbeitet</div>
                </div>
                <div className="result-card skip">
                  <div className="result-value">{result.summary?.skipped || 0}</div>
                  <div className="result-label">Übersprungen</div>
                </div>
                <div className="result-card info">
                  <div className="result-value">{result.summary?.total_rows || 0}</div>
                  <div className="result-label">Excel-Zeilen</div>
                </div>
                <div className="result-card db">
                  <div className="result-value">{result.summary?.db_inserted || 0}</div>
                  <div className="result-label">DB eingefügt</div>
                </div>
                <div className="result-card db-skip">
                  <div className="result-value">{result.summary?.db_skipped || 0}</div>
                  <div className="result-label">DB Duplikate</div>
                </div>
              </div>

              {/* CSV-Archiv Info */}
              {result.csv_archive && (
                <div className="csv-archive-info">
                  <span className="archive-icon">📦</span>
                  <span className="archive-text">
                    CSV-Dateien archiviert: <strong>{result.csv_archive}</strong>
                  </span>
                </div>
              )}

              {/* Processed List */}
              {result.processed?.length > 0 && (
                <div className="result-section">
                  <h4>✓ Erfolgreich verarbeitet</h4>
                  <ul className="result-list">
                    {result.processed.map((item, idx) => (
                      <li key={idx} className="result-item success">
                        <span className="filename">{item.csv}</span>
                        <span className="arrow">→</span>
                        <span className="filename">{item.new_excel}</span>
                        <span className="rows">({item.rows_inserted} Zeilen)</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Welle 76: Nur-DB-Dateien (ohne Excel, direkt in die Datenbank) */}
              {result.db_only?.length > 0 && (
                <div className="result-section">
                  <h4>🗄 Nur in die Datenbank</h4>
                  <ul className="result-list">
                    {result.db_only.map((item, idx) => {
                      const db = (result.db_results || []).find(r => r.filename === item.csv);
                      return (
                        <li key={idx} className="result-item success">
                          <span className="filename">{item.csv}</span>
                          <span className="arrow">→</span>
                          <span className="filename">DB · Konto {item.kontocode || item.iban}</span>
                          <span className="rows">
                            {db?.error
                              ? `Fehler: ${db.error}`
                              : db
                                ? `(${db.inserted} neu, ${db.skipped} Duplikate/Altbestand)`
                                : `(${item.row_count} Zeilen)`}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* Skipped List */}
              {result.skipped?.length > 0 && (
                <div className="result-section">
                  <h4>↷ Übersprungen</h4>
                  <ul className="result-list">
                    {result.skipped.map((item, idx) => (
                      <li key={idx} className="result-item skipped">
                        <span className="filename">{item.csv || item.excel || "–"}</span>
                        <span className="reason">{item.reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="action-buttons">
                <button 
                  className="btn-primary"
                  onClick={onClose}
                >
                  Schließen
                </button>
                <button 
                  className="btn-secondary"
                  onClick={() => { setResult(null); setPreview(null); loadStats(); }}
                >
                  Neuer Batch
                </button>
              </div>
            </div>
          )}

          {/* Trace Log */}
          {trace.length > 0 && (
            <details className="trace-section">
              <summary>Log ({trace.length} Einträge)</summary>
              <ul className="trace-list">
                {trace.map((msg, idx) => (
                  <li key={idx}>{msg}</li>
                ))}
              </ul>
            </details>
          )}
        </div>

        {/* Welle 76: Nur-DB-Bestätigung */}
        {dbOnlyModal && (
          <div className="mapping-modal-overlay">
            <div className="mapping-modal" style={{ maxWidth: 480 }}>
              <div className="mapping-modal-header">
                <h3>🗄 Konto als „Nur DB" merken</h3>
                <button className="close-btn" onClick={() => setDbOnlyModal(null)}>×</button>
              </div>
              <div style={{ padding: "14px 18px" }}>
                <p style={{ fontSize: "0.9rem", marginTop: 0 }}>
                  Konto <strong>{dbOnlyModal.csv_kontocode || "?"}</strong>{" "}
                  (<code>{dbOnlyModal.csv_iban}</code>) dauerhaft als Nur-DB-Konto merken?
                </p>
                <p style={{ fontSize: "0.85rem", color: "#555" }}>
                  Künftige CSVs dieses Kontos laufen <strong>automatisch nur in die Datenbank</strong>
                  {" "}(auch im täglichen Auto-Batch), werden danach archiviert — es wird keine
                  Excel-Datei angelegt oder gesucht. Das Merkmal lässt sich hier jederzeit wieder aufheben.
                </p>
                <p style={{ fontSize: "0.8rem", color: "#777" }}>
                  CSV: {dbOnlyModal.csv_filename} · {dbOnlyModal.csv_oldest_date} – {dbOnlyModal.csv_newest_date}
                </p>
                <div className="action-buttons" style={{ marginTop: 12 }}>
                  <button className="btn-primary" onClick={() => setDbOnly(dbOnlyModal, true)} disabled={savingDbOnly}>
                    {savingDbOnly ? "Speichere…" : "Ja, merken"}
                  </button>
                  <button className="btn-secondary" onClick={() => setDbOnlyModal(null)} disabled={savingDbOnly}>
                    Abbrechen
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Neue-Excel-Modal */}
        {createExcelModal && (
          <div className="mapping-modal-overlay">
            <div className="mapping-modal" style={{ maxWidth: 480 }}>
              <div className="mapping-modal-header">
                <h3>Neue Excel anlegen</h3>
                <button className="close-btn" onClick={() => setCreateExcelModal(null)}>×</button>
              </div>
              <div style={{ padding: "14px 18px" }}>
                <p style={{ fontSize: "0.85rem", marginTop: 0 }}>
                  CSV: <strong>{createExcelModal.csv_filename || createExcelModal.csv?.filename}</strong>. Die neue Excel
                  bekommt die IBAN in Zelle A2 — damit erkennt der Batch sie ab sofort
                  automatisch. Danach bitte das Mapping anlegen (Spalte "Auftragskonto"
                  wird automatisch vorbelegt).
                </p>
                {createExcelError && (
                  <p style={{ color: "var(--color-error, #d32f2f)", fontSize: "0.85rem" }}>{createExcelError}</p>
                )}
                <label style={{ display: "block", fontSize: "0.8rem", fontWeight: 600, marginBottom: 4 }}>Dateiname</label>
                <input
                  style={{ width: "100%", padding: "8px 10px", marginBottom: 10, borderRadius: 6 }}
                  value={createExcelForm.filename}
                  onChange={(e) => setCreateExcelForm({ ...createExcelForm, filename: e.target.value })}
                />
                <label style={{ display: "block", fontSize: "0.8rem", fontWeight: 600, marginBottom: 4 }}>Sheet-Name</label>
                <input
                  style={{ width: "100%", padding: "8px 10px", marginBottom: 10, borderRadius: 6 }}
                  value={createExcelForm.sheet_name}
                  onChange={(e) => setCreateExcelForm({ ...createExcelForm, sheet_name: e.target.value })}
                />
                <label style={{ display: "block", fontSize: "0.8rem", fontWeight: 600, marginBottom: 4 }}>IBAN</label>
                <input
                  style={{ width: "100%", padding: "8px 10px", marginBottom: 16, borderRadius: 6, fontFamily: "monospace" }}
                  value={createExcelForm.iban}
                  onChange={(e) => setCreateExcelForm({ ...createExcelForm, iban: e.target.value })}
                  placeholder="DE00 0000 0000 0000 0000 00"
                />
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                  <button className="btn-small btn-mapping" onClick={createExcelHandler} disabled={creatingExcel}>
                    {creatingExcel ? "Erstelle..." : "Excel anlegen und Mapping öffnen"}
                  </button>
                  <button className="btn-small" onClick={() => setCreateExcelModal(null)} disabled={creatingExcel}>
                    Abbrechen
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Mapping Modal */}
        {mappingModal && (
          <div className="mapping-modal-overlay">
            <div className="mapping-modal">
              <div className="mapping-modal-header">
                <h3>Mapping erstellen</h3>
                <button className="close-btn" onClick={closeMappingModal}>×</button>
              </div>
              
              <div className="mapping-modal-info">
                <p>
                  <strong>CSV:</strong> {mappingModal.csv_filename}<br/>
                  <strong>Excel:</strong> {mappingModal.excel_filename} → {mappingModal.excel_sheet}
                </p>
              </div>

              {mappingFields ? (
                <div className="mapping-form">
                  <table className="mapping-table">
                    <thead>
                      <tr>
                        <th>Excel-Spalte</th>
                        <th>→</th>
                        <th>CSV-Spalte</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mappingFields.excel_headers.map((header) => (
                        <tr key={header}>
                          <td>{header}</td>
                          <td>→</td>
                          <td>
                            <select
                              value={mappingValues[header] || ""}
                              onChange={(e) => handleMappingChange(header, e.target.value)}
                            >
                              <option value="">-- Auswählen --</option>
                              {mappingFields.csv_headers.map((csvHeader) => (
                                <option key={csvHeader} value={csvHeader}>
                                  {csvHeader}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="mapping-loading">
                  <div className="loading-spinner medium"></div>
                  <div className="loading-text">Lade Felder...</div>
                </div>
              )}

              <div className="mapping-modal-actions">
                <button 
                  className="btn-primary"
                  onClick={saveMappingHandler}
                  disabled={savingMapping}
                >
                  {savingMapping ? (
                    <span className="btn-loading">
                      <div className="loading-spinner small"></div>
                      Speichere...
                    </span>
                  ) : "Mapping speichern"}
                </button>
                <button 
                  className="btn-secondary"
                  onClick={closeMappingModal}
                  disabled={savingMapping}
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Admin Modal */}
        {adminModalOpen && (
          <div className="admin-modal-overlay">
            <div className="admin-modal">
              <div className="admin-modal-header">
                <h3>⚙️ Admin-Funktionen</h3>
                <button 
                  className="close-btn" 
                  onClick={() => setAdminModalOpen(false)}
                >
                  ×
                </button>
              </div>

              <div className="admin-modal-content">
                {/* 1. Ganzes Jahr ersetzen */}
                {preview?.summary?.full_year_detected > 0 && (
                  <div className="admin-section">
                    <h4>📅 Ganzes Jahr ersetzen</h4>
                    <label className="admin-checkbox">
                      <input
                        type="checkbox"
                        checked={replaceFullYear}
                        onChange={(e) => setReplaceFullYear(e.target.checked)}
                      />
                      <span>
                        Jahr komplett ersetzen ({preview.summary.full_year_detected} erkannt)
                      </span>
                    </label>
                    <p className="admin-hint">
                      Bei Aktivierung werden alle Zeilen des Jahres in der Excel gelöscht 
                      und durch die kompletten CSV-Daten ersetzt.
                    </p>
                  </div>
                )}

                {/* 2. Validierung */}
                <div className="admin-section">
                  <h4>🔍 Daten-Validierung</h4>
                  <p className="admin-hint">
                    Prüft ob alle CSV-Zeilen (Datum + Betrag) in den Excel-Dateien vorhanden sind.
                    Die CSV gilt als Referenz - fehlende Einträge werden aufgelistet.
                  </p>
                  
                  <button 
                    className="btn-validate"
                    onClick={runValidation}
                    disabled={validating}
                  >
                    {validating ? (
                      <span className="btn-loading">
                        <div className="loading-spinner small"></div>
                        Validiere...
                      </span>
                    ) : "Validierung starten"}
                  </button>

                  {/* Validierungs-Ergebnis */}
                  {validationResult && (
                    <div className="validation-result">
                      <div className="validation-summary">
                        <span className={`validation-badge ${validationResult.summary?.invalid === 0 ? 'valid' : 'invalid'}`}>
                          {validationResult.summary?.invalid === 0 
                            ? `✅ Alle ${validationResult.summary?.total} Dateien OK`
                            : `❌ ${validationResult.summary?.invalid} von ${validationResult.summary?.total} mit Fehlern`
                          }
                        </span>
                        {validationResult.summary?.total_missing_rows > 0 && (
                          <span className="validation-missing-count">
                            {validationResult.summary.total_missing_rows} Zeilen fehlen insgesamt
                          </span>
                        )}
                      </div>

                      {/* Detail-Liste */}
                      <div className="validation-details">
                        {validationResult.results?.map((res, idx) => (
                          <div 
                            key={idx} 
                            className={`validation-item ${res.valid ? 'valid' : 'invalid'}`}
                          >
                            <div className="validation-item-header">
                              <span className="validation-status">
                                {res.valid ? '✅' : '❌'}
                              </span>
                              <span className="validation-files">
                                {res.csv_file} → {res.excel_file} / {res.sheet}
                              </span>
                              <span className="validation-counts">
                                {res.matched_count}/{res.csv_count} gefunden
                              </span>
                            </div>
                            
                            {!res.valid && res.missing_rows?.length > 0 && (
                              <div className="validation-missing">
                                <div className="missing-header">
                                  Fehlende Zeilen ({res.total_missing || res.missing_rows.length}):
                                </div>
                                <ul className="missing-list">
                                  {res.missing_rows.slice(0, 10).map((row, rowIdx) => (
                                    <li key={rowIdx}>
                                      <span className="missing-date">{row.date}</span>
                                      <span className="missing-amount">{row.amount?.toFixed(2)} €</span>
                                      <span className="missing-text">{row.text}</span>
                                    </li>
                                  ))}
                                  {res.missing_rows.length > 10 && (
                                    <li className="missing-more">
                                      ... und {res.missing_rows.length - 10} weitere
                                    </li>
                                  )}
                                </ul>
                              </div>
                            )}
                            
                            {res.error && (
                              <div className="validation-error">
                                Fehler: {res.error}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="admin-modal-footer">
                <button 
                  className="btn-secondary"
                  onClick={() => setAdminModalOpen(false)}
                >
                  Schließen
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default ExcelBatchPage;
